import React, { useEffect, useCallback, useState, useRef } from 'react';

/**
 * ParticlesBackground — site-wide ambient backdrop.
 *
 * Behaviour:
 * - Hidden by default. Fades in only while the user is actively scrolling.
 * - Fades back out ~600ms after scroll input stops.
 * - mix-blend-multiply keeps it firmly in the background of words/images.
 * - Respects `prefers-reduced-motion` (no animation, no fade).
 */
const FADE_OUT_DELAY = 600; // ms of scroll-idle before fading out

const ParticlesBackground = () => {
  const [visible, setVisible] = useState(false);
  const idleTimerRef = useRef(0);

  const initParticles = useCallback(() => {
    // Tear down any previous instance (HMR / route changes)
    const oldCanvas = document.querySelector('#particles-js canvas');
    if (oldCanvas) oldCanvas.remove();
    if (window.pJSDom?.length > 0) {
      try {
        window.pJSDom.forEach((p) => p.pJS.fn.vendors.destroypJS());
      } catch (_) {}
      window.pJSDom = [];
    }

    if (!window.particlesJS) return;

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    // Brand teal — Genora primary
    const colors = {
      particles: '#0d9488', // teal-600
      lines: '#14b8a6',     // teal-500
      accent: '#0f766e',    // teal-700
    };

    window.particlesJS('particles-js', {
      particles: {
        number: { value: reduceMotion ? 40 : 75, density: { enable: true, value_area: 1000 } },
        color: { value: colors.particles },
        shape: { type: 'circle', stroke: { width: 0, color: colors.accent } },
        opacity: {
          value: 0.55,
          random: true,
          anim: { enable: !reduceMotion, speed: 0.6, opacity_min: 0.2 },
        },
        size: {
          value: 2.8,
          random: true,
          anim: { enable: !reduceMotion, speed: 1.2, size_min: 0.6 },
        },
        line_linked: {
          enable: true,
          distance: 150,
          color: colors.lines,
          opacity: 0.35,
          width: 1,
        },
        move: {
          enable: !reduceMotion,
          speed: 0.9,
          random: true,
          out_mode: 'out',
        },
      },
      // Cursor "wake": nearby particles connect via lines toward the
      // mouse position (grab mode). detect_on:'window' is required since
      // the canvas itself has pointer-events:none. Click is left disabled
      // so the cursor never spawns or scatters particles.
      interactivity: {
        detect_on: 'window',
        events: {
          onhover: { enable: !reduceMotion, mode: 'grab' },
          onclick: { enable: false },
          resize: true,
        },
        modes: {
          grab: {
            distance: 180,
            line_linked: { opacity: 0.55 },
          },
        },
      },
      retina_detect: true,
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let cancelled = false;
    const SCRIPT_ID = 'particles-js-cdn';

    const ensureScript = () =>
      new Promise((resolve) => {
        if (window.particlesJS) return resolve();
        const existing = document.getElementById(SCRIPT_ID);
        if (existing) {
          existing.addEventListener('load', () => resolve(), { once: true });
          return;
        }
        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = 'https://cdn.jsdelivr.net/particles.js/2.0.0/particles.min.js';
        script.async = true;
        script.onload = () => resolve();
        document.body.appendChild(script);
      });

    ensureScript().then(() => {
      if (cancelled) return;
      initParticles();
    });

    // ---- Visibility tied to user activity (scroll OR mouse movement) ----
    // Show particles whenever the user is actively scrolling OR moving the
    // mouse. After FADE_OUT_DELAY ms of zero input, fade them back out.
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    let isVisible = false;

    const onActivity = () => {
      if (!isVisible) {
        isVisible = true;
        setVisible(true);
      }
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = window.setTimeout(() => {
        isVisible = false;
        setVisible(false);
      }, FADE_OUT_DELAY);
    };

    if (!reduceMotion) {
      // `passive: true` keeps scrolling smooth on touch / trackpads.
      window.addEventListener('scroll', onActivity, { passive: true });
      window.addEventListener('wheel', onActivity, { passive: true });
      window.addEventListener('touchmove', onActivity, { passive: true });
      window.addEventListener('mousemove', onActivity, { passive: true });
    } else {
      // For reduced-motion users keep particles statically visible.
      setVisible(true);
    }

    return () => {
      cancelled = true;
      window.removeEventListener('scroll', onActivity);
      window.removeEventListener('wheel', onActivity);
      window.removeEventListener('touchmove', onActivity);
      window.removeEventListener('mousemove', onActivity);
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
      if (window.pJSDom?.length > 0) {
        try {
          window.pJSDom.forEach((p) => p.pJS.fn.vendors.destroypJS());
        } catch (_) {}
        window.pJSDom = [];
      }
    };
  }, [initParticles]);

  return (
    <div
      id="particles-js"
      aria-hidden="true"
      data-testid="particles-bg"
      data-visible={visible ? 'true' : 'false'}
      // -z-10 places the canvas BEHIND every element in normal document
      // flow. Page wrappers and cards (bg-slate-50 / bg-white) naturally
      // occlude the particles within their bounds, so they never appear
      // over text or products. Body is set to transparent in index.css so
      // particles can show through wherever a page leaves a gap.
      className={`pointer-events-none fixed inset-0 -z-10 transition-opacity duration-500 ease-out ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
    />
  );
};

export default ParticlesBackground;
